import React, { useEffect, useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  InputLabel,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { MdArrowBackIos, MdCameraAlt, MdHome } from 'react-icons/md';
import { FaImage } from 'react-icons/fa6';
import {
  filter,
  find,
  findIndex,
  isEmpty,
  isEqual,
  map,
  omit,
  sortBy,
  startsWith,
  sum,
  uniq,
  uniqBy,
} from 'lodash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import itemDeliveryService from '../../services/itemDeliveryService';
import eventService from '../../services/eventService';
import itemService from '../../services/itemService';
import fileUploadService from '../../services/fileUploadService';
import itemTypeService from '../../services/itemTypeService';
import AdminHeader from '../../components/AdminHeader';
import { ImageModal } from '../supervisor/SupervisorForm';

const PickupForm = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [file, setFile] = useState(null);
  const [imageUploading, setImageLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [itemPickup, setItemPickup] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [openModal, setOpenModal] = useState(false); // Modal visibility state
  const [selectedImage, setSelectedImage] = useState(null); // Store the item to be edited
  // const [balanceError, setBalanceError] = useState(false); // Track balance error

  const handleFileChange = async (event) => {
    try {
      setImageLoading(true);

      const file = event.target.files[0];
      const newFile = new File([file], `Pickup_photo_${file?.name}`, {
        type: file.type,
      });
      setSelectedFile(newFile);
      const res = await fileUploadService?.handleFileUpload(newFile);
      if (res?.status === 200) {
        setFile(`${res?.data?.filename}`);
      }
    } catch {
      setImageLoading(false);
    } finally {
      setImageLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  const handleSubmit = async () => {
    const photosToAdd = Array.isArray(file) ? file : [file];

    const data = map(
      map(uniqBy(allocations, 'id'), (data) => {
        return {
          ...data,
          photo: data.photo
            ? `${data.photo}, ${photosToAdd.join(', ')}`
            : photosToAdd.join(', '),
        };
      }),
      (allocation) => omit(allocation, ['timestamp', 'entered_qty', 'station'])
    );
    try {
      const res = await itemDeliveryService?.updateItemDelivery(
        selectedDepartment,
        map(data, (i) => {
          return {
            id: i?.id,
            user_id: i?.user_id,
            item_id: i?.item_id,
            allocated_qty: i?.allocated_qty,
            delivered_qty: i?.delivered_qty,
            picked_qty: i?.picked_qty,
            photo: i?.photo,
            department_id: i?.department_id,
            event_id: i?.event_id,
            remarks: 'picked',
            // station: selectedDepartment,
            station_type: data?.station_type,
            user_id: parseInt(localStorage?.getItem('user_id')),
          };
        })
      );
      if (res?.status === 200) {
        toast?.success('Data submitted successfully');
        navigate('/picked-up-items');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message);
    }
  };

  const fetchDepartment = async () => {
    const res = await eventService?.getEventDetail();
    if (res?.status === 200) {
      setDepartments(
        filter(res?.data, {
          event_id: parseInt(localStorage?.getItem('event_id')),
        })
      );
    }
  };

  const fetchItemTypes = async () => {
    const response = await itemTypeService.getItemTypes();
    setItemTypes(response.data);
  };

  const fetchItems = async () => {
    const response = await itemService.getItemDetails();
    setItems(response.data);
  };

  const [loading, setLoading] = useState(false);

  const fetchItemDeliveries = async () => {
    try {
      setLoading(true); // start loading

      const response = await itemDeliveryService.getItemDeliveries(
        localStorage?.getItem('event_id')
      );

      if (selectedDepartment) {
        setItemPickup(
          sortBy(
            filter(
              response.data,
              (data) =>
                data?.station === selectedDepartment &&
                data?.event_id ===
                  parseInt(localStorage?.getItem('event_id')) &&
                data?.delivered_qty !== 0
            ),
            'item_name'
          )
        );
      } else {
        setDepartments(
          uniqBy(
            filter(response.data, {
              event_id: parseInt(localStorage?.getItem('event_id')),
            }),
            'station'
          )
        );
      }
    } catch (error) {
      console.error('Error fetching item deliveries:', error);
    } finally {
      setLoading(false); // stop loading regardless of success or failure
    }
  };

  // const fetchItemDeliveries = async () => {
  //   const response = await itemDeliveryService.getItemDeliveries(
  //     localStorage?.getItem('event_id')
  //   );
  //   if (selectedDepartment) {
  //     setItemPickup(
  //       sortBy(
  //         filter(
  //           response.data,
  //           (data) =>
  //             data?.station === selectedDepartment &&
  //             data?.event_id === parseInt(localStorage?.getItem('event_id')) &&
  //             data?.delivered_qty !== 0
  //         ),
  //         'item_name'
  //       )
  //     );
  //   } else {
  //     setDepartments(
  //       uniqBy(
  //         filter(response.data, {
  //           event_id: parseInt(localStorage?.getItem('event_id')),
  //         }),
  //         'station'
  //       )
  //     );
  //   }
  // };

  useEffect(() => {
    fetchItemDeliveries();
    setAllocations([]);
  }, [selectedDepartment]);

  useEffect(() => {
    fetchItems();
  }, []);

  const handleInputChange = (id, value, item) => {
    const quantity = parseInt(value, 10);

    const newAllocation = {
      ...item,
      picked_qty: quantity,
    };

    const existingIndex = findIndex(allocations, { id: id });
    if (existingIndex >= 0) {
      const updatedAllocations = [...allocations];
      updatedAllocations[existingIndex] = newAllocation;
      setAllocations(updatedAllocations);
    } else {
      setAllocations([...allocations, newAllocation]);
    }
  };

  const checkBalanceError = (item) => {
    const enteredValue =
      item?.entered_qty !== undefined &&
      item?.entered_qty !== null &&
      item?.entered_qty !== ''
        ? item?.entered_qty
        : item?.picked_qty;

    const remainingQty = item?.delivered_qty - enteredValue;

    return remainingQty < 0;
  };

  // useEffect(() => {
  //   const hasNegativeBalance = itemPickup?.some(checkBalanceError);
  //   setBalanceError(hasNegativeBalance);
  // }, [allocations]);

  return (
    <>
      <AdminHeader title='OUTWARD PICKED-UP' />
      <div className='page-content-wrapper scrollable'>
        <Paper elevation={2} sx={{ padding: 2, borderRadius: 2, m: 1 }}>
          {/* <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '1rem',
                color: '#333',
                fontWeight: 550,
              }}
            >
              <MdHome
                fill='#0d970d'
                size={24}
                style={{ cursor: 'pointer', marginRight: '8px' }}
                onClick={() => navigate(`/userAccess`)}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  textDecoration: 'underline',
                  fontSize: '1.1rem',
                }}
              >
                OUTWARD ITEM
              </div>
            </Typography>
            
          </Box> */}

          <Box component='form'>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant='body1'
                sx={{ fontWeight: 'bold', fontSize: '0.675rem' }}
              >
                SELECT STATION
              </Typography>
              <Select
                name='department_id'
                value={selectedDepartment}
                fullWidth
                onChange={(e) => {
                  setSelectedDepartment(e?.target?.value);
                  setAllocations([]);
                  setItemPickup([]);
                  setSelectedFile(null);
                  setFile(null);
                }}
                variant='outlined'
                sx={{ mb: 1, fontSize: '0.675rem' }}
              >
                {!isEmpty(departments) ? (
                  map(departments, (option) => (
                    <MenuItem
                      key={option?.id}
                      value={option?.station}
                      sx={{ fontSize: '0.675rem' }}
                    >
                      {option?.station}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem sx={{ fontSize: '0.675rem' }}>
                    No data found
                  </MenuItem>
                )}
              </Select>
              {!selectedDepartment && (
                <Typography
                  variant='caption'
                  sx={{ fontSize: '0.7rem', color: 'red' }}
                >
                  Station is required
                </Typography>
              )}
            </Box>
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={loading}
            >
              <CircularProgress color='inherit' />
            </Backdrop>
            {selectedDepartment && (
              <>
                {itemPickup.map((item) => {
                  // const isError = checkBalanceError(item);

                  return (
                    <Box key={item.id} sx={{ mb: 2 }}>
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center' // Ensure all fields are aligned center vertically
                        sx={{ mt: 1 }}
                      >
                        {/* Item Name and Type */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '25%', // Set a fixed width to keep consistent spacing
                          }}
                        >
                          <InputLabel
                            htmlFor={`item-${item.id}`}
                            sx={{ fontSize: '0.675rem' }}
                          >
                            PRODUCT
                          </InputLabel>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: '550 !important',
                              pt: 1,
                              fontSize: '0.675rem',
                            }}
                          >
                            {item?.item_name}
                          </Typography>
                        </Box>

                        {/* Delivered Qty */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '25%', // Set width to match others
                          }}
                        >
                          <InputLabel
                            htmlFor={`delivered_qty_${item.id}`}
                            sx={{ fontSize: '0.675rem' }}
                          >
                            DELIVERED QTY
                          </InputLabel>
                          <TextField
                            id={`delivered_qty_${item.id}`}
                            type='number'
                            disabled
                            inputProps={{ min: 0 }}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                textAlign: 'right',
                              },
                            }}
                            value={item?.delivered_qty}
                            variant='outlined'
                            size='small'
                          />
                        </Box>

                        {/* Prev Picked-up Qty */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '25%', // Same width for consistency
                          }}
                        >
                          <InputLabel
                            htmlFor={`another_field_${item.id}`}
                            sx={{ fontSize: '0.675rem' }}
                          >
                            PREV PICKED-UP QTY
                          </InputLabel>
                          <TextField
                            id={`another_field_${item.id}`}
                            type='number'
                            disabled
                            inputProps={{ min: 0 }}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                textAlign: 'right',
                              },
                            }}
                            value={item?.picked_qty || 0}
                            variant='outlined'
                            size='small'
                          />
                        </Box>

                        {/* Current Pick-up Qty */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '25%', // Consistent width with others
                          }}
                        >
                          <InputLabel
                            htmlFor={`pick_up_${item.id}`}
                            sx={{ fontSize: '0.675rem' }}
                          >
                            CURRENT PICKED-UP QTY
                          </InputLabel>
                          <TextField
                            id={`pick_up_${item.id}`}
                            type='number'
                            // disabled={item?.allocated_qty === item?.picked_qty}
                            onChange={(e) => {
                              const newValue = sum([
                                item?.picked_qty,
                                Number(e.target.value),
                              ]);
                              handleInputChange(item.id, newValue, item);
                              item.entered_qty = newValue;
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #1876D1',
                              },
                              '& .MuiOutlinedInput-input': {
                                textAlign: 'right',
                              },
                            }}
                            variant='outlined'
                            size='small'
                          />
                          {/* {isError && (
                            <Typography
                              variant='caption'
                              sx={{ fontSize: '0.7rem', color: 'red' }}
                            >
                              Please eneter valid value
                            </Typography>
                          )} */}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
            {selectedDepartment &&
              (imageUploading ? (
                <div>
                  <CircularProgress size={22} />
                </div>
              ) : (
                <Box
                  display='flex'
                  justifyContent='space-between'
                  gap={1}
                  sx={{ mt: 3 }}
                >
                  {file && (
                    <Box
                      component='img'
                      src={URL.createObjectURL(selectedFile)}
                      alt='Selected'
                      onClick={() => {
                        setOpenModal(true);
                        setSelectedImage(file);
                      }}
                      sx={{
                        maxWidth: 50,
                        maxHeight: 50,
                        borderRadius: 1,
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                    />
                  )}
                  <Box
                    display='flex'
                    justifyContent='flex-end'
                    gap={1}
                    sx={{ mt: 3, width: '100%' }}
                  >
                    <input
                      accept='image/*'
                      style={{ display: 'none' }}
                      id='upload-file'
                      type='file'
                      onChange={handleFileChange}
                    />
                    <Button
                      component='label'
                      htmlFor='upload-file'
                      disabled={isEmpty(itemPickup)}
                      variant='contained'
                      sx={{ textTransform: 'none', fontSize: '0.675rem' }}
                    >
                      UPLOAD FILE
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      disabled={
                        isEmpty(itemPickup) || loading || isEmpty(allocations)
                      }
                      onClick={handleSubmit}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.675rem',
                      }}
                    >
                      SUBMIT
                    </Button>
                  </Box>
                </Box>
              ))}

            {/* <Box sx={{ mt: 1 }}>
             
            </Box> */}
          </Box>
        </Paper>
      </div>
      {openModal && (
        <ImageModal
          open={openModal}
          handleClose={handleCloseModal}
          imageUrl={selectedImage}
        />
      )}
    </>
  );
};

export default PickupForm;
